import React, { useState } from 'react';
import { MoreHorizontal, Loader2, Download, Trash2, Eye, ChevronLeft, ChevronRight } from 'lucide-react';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from 'components/ui/table';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from 'components/ui/dropdown-menu';
import { Button } from 'components/ui/button';
import { Skeleton } from 'components/ui/skeleton';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from 'components/ui/dialog';
import styles from '../MyLibrary.module.scss';

const ExportList = ({ reports, loading, onDownload, downloadingId, onDelete, onViewDetails }) => {
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 15;
  const [deleteDialogId, setDeleteDialogId] = useState(null);

  if (loading) {
    return (
      <div className="p-6 bg-white rounded-lg border border-gray-200 mt-4 shadow-sm">
        <div className="space-y-4">
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-16 w-full" />
          <Skeleton className="h-16 w-full" />
          <Skeleton className="h-16 w-full" />
          <Skeleton className="h-16 w-full" />
          <Skeleton className="h-16 w-full" />
        </div>
      </div>
    );
  }

  const totalPages = Math.ceil(reports.length / pageSize);
  const paginatedReports = reports.slice((currentPage - 1) * pageSize, currentPage * pageSize);

  return (
    <div className="bg-white rounded-lg border border-gray-200 mt-4 shadow-sm overflow-hidden flex flex-col">
      <div className="overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow className="bg-gray-50/50">
              <TableHead className="w-[35%]">Export Name</TableHead>
              <TableHead className="w-[15%]">Source</TableHead>
              <TableHead className="w-[10%]">Format</TableHead>
              <TableHead className="w-[15%]">Created At</TableHead>
              <TableHead className="w-[15%]">Status</TableHead>
              <TableHead className="w-[10%] text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {paginatedReports.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} className="h-32 text-center text-gray-500">
                  No exports found matching your criteria.
                </TableCell>
              </TableRow>
            ) : (
              paginatedReports.map((record) => {
                const canDownload = record.status === 'completed' || record.status === 'ready';
                const canDelete = ['ready', 'failed', 'completed'].includes(record.status);
                const isDownloading = downloadingId === record.id;

                return (
                  <TableRow key={record.id} className="group">
                    <TableCell className="font-medium text-gray-900">
                      <div className="flex items-center gap-2">
                        {record.title}
                        {record.is_seen === false && (
                          <span className="text-[10px] bg-explore/10 text-explore px-1.5 py-0.5 rounded-full font-semibold">
                            New
                          </span>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="text-gray-500 capitalize">{record.source}</TableCell>
                    <TableCell className="text-gray-500">{(record.export_format || 'PDF').toUpperCase()}</TableCell>
                    <TableCell className="text-gray-500">
                      {new Date(record.createdAt).toLocaleString(undefined, { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit', timeZoneName: 'short' })}
                    </TableCell>
                    <TableCell>
                      <span className={`${styles['status-badge']} ${styles[record.status] || ''}`}>
                        {record.status}
                      </span>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-2">
                        <Button
                          variant="secondary"
                          size="sm"
                          className="h-8 text-xs font-medium"
                          disabled={!canDownload || isDownloading}
                          onClick={() => onDownload(record)}
                        >
                          {isDownloading ? (
                            <>
                              <Loader2 className="mr-1.5 h-3 w-3 animate-spin" />
                              Downloading...
                            </>
                          ) : (
                            <>
                              <Download className="mr-1.5 h-3 w-3" />
                              Download
                            </>
                          )}
                        </Button>
                        
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon" className="h-8 w-8 text-gray-500 hover:text-gray-900">
                              <MoreHorizontal className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end" className="w-40 bg-white">
                            <DropdownMenuItem onClick={() => onViewDetails(record)}>
                              <Eye className="mr-2 h-4 w-4 text-gray-500" />
                              View details
                            </DropdownMenuItem>
                            <DropdownMenuSeparator className="bg-gray-100" />
                            
                            <Dialog open={deleteDialogId === record.id} onOpenChange={(open) => !open && setDeleteDialogId(null)}>
                              <DialogTrigger asChild>
                                <DropdownMenuItem 
                                  className="text-red-600 focus:text-red-700 focus:bg-red-50"
                                  disabled={!canDelete}
                                  onSelect={(e) => {
                                    e.preventDefault();
                                    if (canDelete) setDeleteDialogId(record.id);
                                  }}
                                >
                                  <Trash2 className="mr-2 h-4 w-4 text-red-500" />
                                  Delete
                                </DropdownMenuItem>
                              </DialogTrigger>
                              <DialogContent className="sm:max-w-[425px] bg-white">
                                <DialogHeader>
                                  <DialogTitle>Delete export?</DialogTitle>
                                  <DialogDescription className="mt-2">
                                    This will permanently remove "{record.title}".
                                    <br />
                                    This action cannot be undone.
                                  </DialogDescription>
                                </DialogHeader>
                                <DialogFooter className="mt-4 gap-2 sm:gap-0">
                                  <Button variant="outline" onClick={() => setDeleteDialogId(null)}>Cancel</Button>
                                  <Button variant="destructive" onClick={() => {
                                    onDelete(record.id);
                                    setDeleteDialogId(null);
                                  }}>
                                    Delete export
                                  </Button>
                                </DialogFooter>
                              </DialogContent>
                            </Dialog>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </div>
      
      {totalPages > 1 && (
        <div className="flex items-center justify-between px-4 py-3 border-t border-gray-200">
          <div className="text-sm text-gray-500">
            Showing <span className="font-medium text-gray-900">{((currentPage - 1) * pageSize) + 1}</span> to <span className="font-medium text-gray-900">{Math.min(currentPage * pageSize, reports.length)}</span> of <span className="font-medium text-gray-900">{reports.length}</span> results
          </div>
          <div className="flex items-center gap-1">
            <Button
              variant="outline"
              size="icon"
              className="h-8 w-8 bg-white"
              onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
              disabled={currentPage === 1}
            >
              <ChevronLeft className="h-4 w-4 text-gray-600" />
            </Button>
            <div className="text-sm font-medium px-2 text-gray-600">
              {currentPage} / {totalPages}
            </div>
            <Button
              variant="outline"
              size="icon"
              className="h-8 w-8 bg-white"
              onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
              disabled={currentPage === totalPages}
            >
              <ChevronRight className="h-4 w-4 text-gray-600" />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ExportList;
